import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTheme } from '@/contexts/ThemeContext';

const CARD_CLASS = 'bg-white dark:bg-[var(--surface)] shadow-card flex flex-col rounded-card lg:h-full';

const YELLOW = '#ffffff';

export function ThinDonutCard({ title, data, className = '' }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <Card className={`${CARD_CLASS} ${className}`} data-testid="donut-card">
      <CardHeader className="pb-1 pt-5 px-6 shrink-0">
        <CardTitle className="section-title">{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-5 pb-4 pt-0 flex-1 min-h-0 flex items-center overflow-hidden">
        {data.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500 text-sm text-center w-full py-8">No data</p>
        ) : (
          <div className="flex items-center w-full gap-4 h-full min-h-0">
            {/* Donut */}
            <div className="w-[38%] max-w-[180px] max-h-full aspect-square shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    innerRadius="65%"
                    outerRadius="85%"
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {data.map((entry, i) => (
                      <Cell key={i} fill={entry.color || YELLOW} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? '#252930' : '#fff',
                      border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'}`,
                      borderRadius: 12,
                      color: isDark ? '#fff' : '#111',
                      boxShadow: isDark ? 'none' : '0 4px 12px rgba(0,0,0,0.1)',
                      fontSize: 12,
                    }}
                    formatter={(val, name) => [val, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex-1 space-y-1.5 min-w-0 max-h-full overflow-y-auto pe-1">
              {data.map((item, i) => {
                const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                const dotColor = item.color || YELLOW;
                const dotOpacity = 1;
                return (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dotColor, opacity: dotOpacity }} />
                    <span className="text-[11px] lg:text-xs text-gray-600 dark:text-gray-400 truncate flex-1">{item.name}</span>
                    <span className="text-[11px] lg:text-xs font-bold text-gray-800 dark:text-white tabular-nums shrink-0">{item.value}</span>
                    <span className="text-[9px] lg:text-[10px] text-gray-400 dark:text-gray-500 tabular-nums shrink-0 w-[28px] text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
